import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Trophy, XCircle, Loader2 } from "lucide-react";

const WIN_CATEGORIES = [
  { value: "signed", label: "Client Signed Contract" },
  { value: "value_fit", label: "Strong Value Fit" },
  { value: "competitive_win", label: "Won Over Competitor" },
  { value: "referral", label: "Referral / Warm Intro" },
  { value: "quick_close", label: "Fast Sales Cycle" },
  { value: "other_win", label: "Other" },
];

const LOSS_CATEGORIES = [
  { value: "budget", label: "Budget / Pricing" },
  { value: "timing", label: "Bad Timing / Not Ready" },
  { value: "competitor", label: "Chose Competitor" },
  { value: "no_decision", label: "No Decision Made" },
  { value: "scope", label: "Scope Mismatch" },
  { value: "champion_left", label: "Champion Left Company" },
  { value: "went_silent", label: "Went Silent / Ghosted" },
  { value: "other_loss", label: "Other" },
];

export default function CloseReasonModal({ isWin, leadName, company, onConfirm, onCancel }) {
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const categories = isWin ? WIN_CATEGORIES : LOSS_CATEGORIES;

  const handleConfirm = async () => {
    if (!category) return;
    setSaving(true);
    await onConfirm({
      close_category: category,
      close_reason: reason || categories.find(c => c.value === category)?.label || category,
      close_notes: notes,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white border border-slate-200 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="close-reason-modal">
        {/* Header */}
        <div className={`p-5 flex items-center gap-3 ${isWin ? "bg-emerald-50 border-b border-emerald-200" : "bg-red-50 border-b border-red-200"}`}>
          {isWin ? <Trophy className="w-6 h-6 text-emerald-600" /> : <XCircle className="w-6 h-6 text-red-500" />}
          <div>
            <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A]">
              {isWin ? "Close as Won" : "Close as Lost"}
            </h3>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans']">{leadName} at {company}</p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Category */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              {isWin ? "Why did we win?" : "Why did we lose?"} *
            </Label>
            <div className="grid grid-cols-2 gap-2" data-testid="close-category-grid">
              {categories.map(c => (
                <button key={c.value} onClick={() => { setCategory(c.value); if (!reason) setReason(c.label); }}
                  className={`p-3 border text-left text-sm font-['IBM_Plex_Sans'] transition-all ${
                    category === c.value
                      ? isWin ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-red-400 bg-red-50 text-red-800"
                      : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  data-testid={`close-cat-${c.value}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reason detail */}
          <div>
            <Label className="text-sm font-medium text-slate-700">Reason (one-liner)</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)}
              placeholder={isWin ? "e.g., They loved the 90-day roadmap" : "e.g., Budget was cut for Q2"}
              className="mt-1.5 rounded-none border-slate-300" data-testid="close-reason-input" />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium text-slate-700">Notes (optional)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={isWin ? "Key factors that led to the win, what worked, champion details..." : "What could we have done differently? Any learnings for next time..."}
              className="mt-1.5 rounded-none border-slate-300 min-h-[70px] text-sm" data-testid="close-notes-input" />
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-slate-200 flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-none font-['Outfit']" data-testid="close-cancel-btn">Cancel</Button>
          <Button onClick={handleConfirm} disabled={!category || saving}
            className={`flex-1 rounded-none font-['Outfit'] ${isWin ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}
            data-testid="close-confirm-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isWin ? "Confirm Win" : "Confirm Loss"}
          </Button>
        </div>
      </div>
    </div>
  );
}
